import { useCallback } from 'react';

import {
  useGetOwnerSupplierShipping,
  usePatchOwnerSupplierShipping,
  usePostOwnerSupplierShipping,
} from '~/hooks/vender';

import { PICKUP_SHIPPING_TYPES } from '~/constants';

import type { TypePickupAddressDataKey, TypePickupAddressFormData, TypePickupData } from '~/types/receive.types';

const PICKUP_ADDRESS_TYPES = {
  KURLY_DEFAULT: 'kurlyDefault',
  KURLY_ETC: 'kurlyEtc',
  MILKRUN_DEFAULT: 'milkrunDefault',
  MILKRUN_ETC1: 'milkrunEtc1',
  MILKRUN_ETC2: 'milkrunEtc2',
} as const;

type TypeDefaultPickupAddressType = 'kurlyDefault' | 'milkrunDefault';
type TypePickupShippingType = (typeof PICKUP_SHIPPING_TYPES)[keyof typeof PICKUP_SHIPPING_TYPES];

/** 픽업 출고지 데이터 타입 */
type TypeDataWithPickupAddress = {
  pickupAddressData?: TypePickupAddressFormData['pickupAddressData'];
};

type TypeUsePickupAddressHandlerParams<T extends TypeDataWithPickupAddress> = {
  /** 공급사 코드 */
  supplierCode: string;
  /** 현재 데이터 */
  currentData: T;
  /** 데이터 변경 콜백 함수 */
  onDataChange: (updatedData: T) => void;
};

/**
 * [입고관리] 픽업 출고지 관련 로직을 관리하는 커스텀 훅
 */
const usePickupAddressHandler = <T extends TypeDataWithPickupAddress>({
  supplierCode,
  currentData,
  onDataChange,
}: TypeUsePickupAddressHandlerParams<T>) => {
  const { requestOwnerSupplierShipping } = useGetOwnerSupplierShipping();
  const { registerOwnerSupplierShipping } = usePostOwnerSupplierShipping();
  const { updateOwnerSupplierShipping } = usePatchOwnerSupplierShipping();

  /** 데이터에서 픽업 출고지 정보를 추출 */
  const getPickupAddressData = useCallback((data: T) => data.pickupAddressData ?? {}, []);

  /** 특정 타입의 픽업 출고지 데이터 업데이트 */
  const updatePickupAddressData = useCallback(
    (data: T, addressType: TypePickupAddressDataKey, updatedData: Partial<TypePickupData> | undefined): T => ({
      ...data,
      pickupAddressData: {
        ...data.pickupAddressData,
        [addressType]: updatedData,
      },
    }),
    []
  );

  /** 기본 출고지 여부 확인 */
  const isDefaultAddress = useCallback(
    (addressType: TypePickupAddressDataKey): addressType is TypeDefaultPickupAddressType => {
      return addressType === PICKUP_ADDRESS_TYPES.KURLY_DEFAULT || addressType === PICKUP_ADDRESS_TYPES.MILKRUN_DEFAULT;
    },
    []
  );

  /** 출고지 타입 반환 (컬리픽업, 밀크런픽업) */
  const getShippingTypeByAddressType = useCallback((addressType: TypeDefaultPickupAddressType) => {
    return addressType === PICKUP_ADDRESS_TYPES.KURLY_DEFAULT
      ? PICKUP_SHIPPING_TYPES.KURLY_PICKUP
      : PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP;
  }, []);

  /** 출고지 정보 API 호출 공통 파라미터 생성 */
  const createShippingParams = useCallback(
    (data: TypePickupData, shippingType: TypePickupShippingType) => ({
      shippingType,
      zipCode: data.zipCode,
      address: data.address,
      detailAddress: data.detailAddress,
      contactNumber: data.contactNumber,
    }),
    []
  );

  /** 기본 출고지 정보 요청 */
  const requestDefaultPickupAddress = useCallback(
    (shippingType: TypePickupShippingType, updatedData: T, onComplete?: (updatedData: T) => void) => {
      const handleDataUpdate = (data: T) => {
        if (onComplete) {
          onComplete(data);
        } else {
          onDataChange(data);
        }
      };

      requestOwnerSupplierShipping(
        {
          ownerSupplierCode: supplierCode,
          shippingType: shippingType,
        },
        {
          onSuccess: (data) => {
            if (Object.keys(data).length > 0) {
              const defaultKey =
                shippingType === PICKUP_SHIPPING_TYPES.KURLY_PICKUP ? 'kurlyDefault' : 'milkrunDefault';

              const currentPickupData = getPickupAddressData(updatedData);
              const updatedPickupData = {
                ...currentPickupData,
                [defaultKey]: {
                  ...(currentPickupData?.[defaultKey] ?? {
                    isUseVehicleSize: false,
                    palletQuantity: '',
                  }),
                  zipCode: data.zipCode,
                  address: data.address,
                  detailAddress: data.detailAddress,
                  contactNumber: data.contactNumber,
                },
              };

              const finalUpdatedData = updatePickupAddressData(updatedData, defaultKey, updatedPickupData[defaultKey]);
              handleDataUpdate(finalUpdatedData);
            } else {
              handleDataUpdate(updatedData);
            }
          },
        }
      );
    },
    [supplierCode, requestOwnerSupplierShipping, getPickupAddressData, updatePickupAddressData, onDataChange]
  );

  /** 기본 출고지 등록/수정 */
  const handleDefaultAddressUpdate = useCallback(
    (addressType: TypeDefaultPickupAddressType, data: TypePickupData, updatedData: T) => {
      const shippingType = getShippingTypeByAddressType(addressType);
      const params = createShippingParams(data, shippingType);
      const currentPickupData = getPickupAddressData(currentData);
      const isExistingAddress = Boolean(currentPickupData?.[addressType]?.address);

      if (isExistingAddress) {
        updateOwnerSupplierShipping(
          {
            ownerSupplierCode: supplierCode,
            shippingType,
            params,
          },
          {
            onSuccess: () => requestDefaultPickupAddress(shippingType, updatedData),
          }
        );
      } else {
        registerOwnerSupplierShipping(
          {
            ownerSupplierCode: supplierCode,
            params,
          },
          {
            onSuccess: () => requestDefaultPickupAddress(shippingType, updatedData),
          }
        );
      }
    },
    [
      supplierCode,
      currentData,
      getShippingTypeByAddressType,
      createShippingParams,
      getPickupAddressData,
      updateOwnerSupplierShipping,
      registerOwnerSupplierShipping,
      requestDefaultPickupAddress,
    ]
  );

  /** 픽업 주소 데이터 변경 핸들러 */
  const handlePickupAddressDataChange = useCallback(
    (
      addressType: TypePickupAddressDataKey,
      key: keyof Pick<TypePickupData, 'address' | 'isUseVehicleSize' | 'vehicleSize' | 'palletQuantity' | 'memo'>,
      data: TypePickupData
    ) => {
      const isAddressChanged = key === 'address';
      const currentPickupData = getPickupAddressData(currentData);

      // 'address' 필드 변경 시 주소 관련 데이터 모두 업데이트
      const updatedPickupAddressData = isAddressChanged
        ? {
            ...currentPickupData?.[addressType],
            zipCode: data.zipCode,
            address: data.address,
            detailAddress: data.detailAddress,
            contactNumber: data.contactNumber,
          }
        : { ...currentPickupData?.[addressType], [key]: data[key] };

      const updatedData = updatePickupAddressData(currentData, addressType, updatedPickupAddressData);

      // 기본 출고지 주소 변경 시 API 호출
      if (isDefaultAddress(addressType) && isAddressChanged) {
        handleDefaultAddressUpdate(addressType, data, updatedData);
        return;
      }

      onDataChange(updatedData);
    },
    [
      currentData,
      getPickupAddressData,
      updatePickupAddressData,
      onDataChange,
      isDefaultAddress,
      handleDefaultAddressUpdate,
    ]
  );

  return {
    handlePickupAddressDataChange,
    requestDefaultPickupAddress,
  };
};

export default usePickupAddressHandler;
